import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Send, Upload } from 'lucide-react';
import { FadeInUp } from '../components/animations';
import { categoryList } from '../data/categoryData';
import SEO from '../components/SEO';
import { breadcrumbSchema } from '../utils/schema';
import { useEmailSubmit } from '../hooks/useEmailSubmit';

const RequestSourcingPage = () => {
  const formRef = useRef();
  const [productImage, setProductImage] = useState(null);
  const [techPack, setTechPack] = useState(null);
  const { isSubmitting, submit } = useEmailSubmit({
    successMessage: 'Sourcing request submitted successfully! Our team will contact you within 24 hours.',
    errorMessage: 'Failed to submit. Please try again or contact us directly.',
    sheetName: 'Sourcing Requests',
  });

  const buildParams = (fd) => ({
    emailParams: {
      name: fd.get('user_name'),
      email: fd.get('user_email'),
      phone: fd.get('whatsapp') || '-',
      message: `Request Type: Sourcing Request
Company: ${fd.get('company')}
Country: ${fd.get('country')}
Product Category: ${fd.get('product_category')}
Quantity: ${fd.get('quantity') || '-'}
Target Price: ${fd.get('target_price') || '-'}
Destination Country: ${fd.get('destination_country') || '-'}
Required Delivery Date: ${fd.get('delivery_date') || '-'}

Product Description:
${fd.get('message')}

Additional Requirements:
${fd.get('additional_requirements') || '-'}`,
    },
    sheetRow: {
      Name: fd.get('user_name'),
      Company: fd.get('company'),
      Country: fd.get('country'),
      Email: fd.get('user_email'),
      WhatsApp: fd.get('whatsapp') || '-',
      'Product Category': fd.get('product_category'),
      'Product Description': fd.get('message'),
      Quantity: fd.get('quantity') || '-',
      'Target Price': fd.get('target_price') || '-',
      'Destination Country': fd.get('destination_country') || '-',
      'Required Delivery Date': fd.get('delivery_date') || '-',
      'Additional Requirements': fd.get('additional_requirements') || '-',
    },
  });

  const handleSubmit = (e) => submit(e, formRef, buildParams, () => {
    setProductImage(null);
    setTechPack(null);
  });

  const fieldClass = "w-full px-4 py-3 bg-white border border-[#E5E0D8] text-sm text-[#1F2937] placeholder:text-[#9CA3AF] focus:border-[#1E3D66] focus:ring-1 focus:ring-[#1E3D66]/20 outline-none transition-all disabled:bg-[#fafaf9] disabled:cursor-not-allowed";
  const labelClass = "block text-xs font-semibold tracking-wider uppercase text-[#0B2A57] mb-2";

  return (
    <div className="pt-20" data-testid="request-sourcing-page">
      <SEO
        title="Request Sourcing from India | Taiga Sourcing"
        description="Submit your sourcing requirement. Taiga Sourcing will identify suitable Indian manufacturers and coordinate the sourcing process for you."
        keywords="request sourcing India, product sourcing request, India sourcing requirement, India buying agent"
        path="/request-sourcing"
        schema={breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Request Sourcing', path: '/request-sourcing' }])}
      />
      <section className="relative py-24 lg:py-32 bg-[#071E45] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <FadeInUp>
            <h1
              className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.1] mb-6"
              style={{ fontFamily: 'Cabinet Grotesk' }}
              data-testid="request-sourcing-title"
            >
              REQUEST SOURCING
            </h1>
            <p className="text-base text-white/70 leading-relaxed max-w-2xl mx-auto">
              Share your requirement with us. We'll help identify suitable Indian manufacturing options.
            </p>
          </FadeInUp>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#fafaf9]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <FadeInUp>
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" data-testid="sourcing-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Name *</label>
                  <input type="text" name="user_name" required disabled={isSubmitting} className={fieldClass} placeholder="Your name" data-testid="sourcing-name" />
                </div>
                <div>
                  <label className={labelClass}>Company *</label>
                  <input type="text" name="company" required disabled={isSubmitting} className={fieldClass} placeholder="Company name" data-testid="sourcing-company" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Country *</label>
                  <input type="text" name="country" required disabled={isSubmitting} className={fieldClass} placeholder="Your country" data-testid="sourcing-country" />
                </div>
                <div>
                  <label className={labelClass}>Email *</label>
                  <input type="email" name="user_email" required disabled={isSubmitting} className={fieldClass} placeholder="email@company.com" data-testid="sourcing-email" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>WhatsApp</label>
                  <input type="tel" name="whatsapp" disabled={isSubmitting} className={fieldClass} placeholder="+1 234 567 8900" data-testid="sourcing-whatsapp" />
                </div>
                <div>
                  <label className={labelClass}>Product Category *</label>
                  <select name="product_category" required disabled={isSubmitting} className={fieldClass} data-testid="sourcing-category">
                    <option value="">Select Category</option>
                    {categoryList.map((cat) => (
                      <option key={cat.slug} value={cat.title}>{cat.title}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelClass}>Product Description *</label>
                <textarea name="message" required disabled={isSubmitting} rows={4} className={fieldClass + " resize-none"} placeholder="Describe the product you want to source..." data-testid="sourcing-description" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Quantity</label>
                  <input type="text" name="quantity" disabled={isSubmitting} className={fieldClass} placeholder="e.g., 5,000 pcs" data-testid="sourcing-quantity" />
                </div>
                <div>
                  <label className={labelClass}>Target Price</label>
                  <input type="text" name="target_price" disabled={isSubmitting} className={fieldClass} placeholder="e.g., USD 5.00/pc" data-testid="sourcing-price" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Destination Country</label>
                  <input type="text" name="destination_country" disabled={isSubmitting} className={fieldClass} placeholder="Shipment destination" data-testid="sourcing-destination" />
                </div>
                <div>
                  <label className={labelClass}>Required Delivery Date</label>
                  <input type="date" name="delivery_date" disabled={isSubmitting} className={fieldClass} data-testid="sourcing-date" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Upload Product Reference</label>
                  <label className="flex items-center gap-3 px-4 py-3 bg-white border border-dashed border-[#E5E0D8] cursor-pointer hover:border-[#1E3D66] transition-colors">
                    <Upload size={18} className="text-[#6B7280]" />
                    <span className="text-sm text-[#6B7280]">{productImage ? productImage.name : 'Choose file...'}</span>
                    <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => setProductImage(e.target.files[0])} data-testid="sourcing-image-upload" />
                  </label>
                </div>
                <div>
                  <label className={labelClass}>Upload Spec / Tech Pack</label>
                  <label className="flex items-center gap-3 px-4 py-3 bg-white border border-dashed border-[#E5E0D8] cursor-pointer hover:border-[#1E3D66] transition-colors">
                    <Upload size={18} className="text-[#6B7280]" />
                    <span className="text-sm text-[#6B7280]">{techPack ? techPack.name : 'Choose file...'}</span>
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => setTechPack(e.target.files[0])} data-testid="sourcing-techpack-upload" />
                  </label>
                </div>
              </div>

              <div>
                <label className={labelClass}>Additional Requirements</label>
                <textarea name="additional_requirements" disabled={isSubmitting} rows={3} className={fieldClass + " resize-none"} placeholder="Any other details or specifications..." data-testid="sourcing-additional" />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: isSubmitting ? 1 : 1.01 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.99 }}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0B2A57] text-white text-sm font-semibold tracking-wider uppercase transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="sourcing-submit"
              >
                {isSubmitting ? 'Submitting...' : (
                  <>Submit Sourcing Request <Send size={18} /></>
                )}
              </motion.button>

              <p className="text-xs text-center text-[#9CA3AF]">
                Files will be reviewed by our team. For large files, please send directly to info@taigasourcing.com
              </p>
            </form>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default RequestSourcingPage;
